import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { Milk, Flame, Sprout, Package, LogOut, ArrowRight } from 'lucide-react';
import api from '../../services/api';

const formatRs = (amount) => {
  if (amount >= 1000000) return `Rs ${(amount / 1000000).toFixed(2)}M`;
  if (amount >= 1000) return `Rs ${(amount / 1000).toFixed(1)}K`;
  return `Rs ${amount.toFixed(0)}`;
};

const MainDashboard = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  const [dairyData, setDairyData] = useState({
    animals: '...',
    todayProduction: '...',
    monthRevenue: '...',
    monthExpenditure: '...',
    netProfit: null,
  });
  const [dairyError, setDairyError] = useState(false);

  const [chemData, setChemData] = useState({
    products: '...',
    stockValue: '...',
    monthRevenue: '...',
    monthExpenditure: '...',
    netProfit: null,
  });
  const [chemError, setChemError] = useState(false);

  const [tapeData, setTapeData] = useState({
    products: '...',
    stockValue: '...',
    monthRevenue: '...',
    monthExpenditure: '...',
    netProfit: null,
  });
  const [tapeError, setTapeError] = useState(false);

  useEffect(() => {
    fetchDairyStats();
    fetchChemStats();
    fetchTapeStats();
  }, []);

  // Net profit comes straight from the endpoint, not
  // revenue - expenditure, because the tape P&L nets COGS off
  // as well. Recomputing it here would overstate profit by the
  // cost of goods and disagree with the P&L tab.
  const fetchTapeStats = async () => {
    setTapeError(false);
    setTapeData({ products: '...', stockValue: '...', monthRevenue: '...', monthExpenditure: '...', netProfit: null });
    try {
      const response = await api.get('/api/tape/dashboard/kpis');
      const s = response.data.data;

      setTapeData({
                        products: s.finished_pieces,
        stockValue: formatRs(parseFloat(s.stock_value) || 0),
        monthRevenue: formatRs(parseFloat(s.revenue) || 0),
        monthExpenditure: formatRs(parseFloat(s.expenditure) || 0),
        netProfit: parseFloat(s.net_profit),
      });
    } catch (error) {
      console.error('Error fetching tape stats:', error);
      setTapeError(true);
      setTapeData({ products: '-', stockValue: '-', monthRevenue: '-', monthExpenditure: '-', netProfit: null });
    }
  };

  const fetchChemStats = async () => {
    setChemError(false);
    setChemData({ products: '...', stockValue: '...', monthRevenue: '...', monthExpenditure: '...', netProfit: null });
    try {
      const response = await api.get('/api/chemical/dashboard/kpis');
      const s = response.data.data;

      const revenue = parseFloat(s.this_month_sales) || 0;
      const expenditure = (parseFloat(s.this_month_purchases) || 0) + (parseFloat(s.this_month_expenses) || 0);

      setChemData({
        products: s.products_count,
        stockValue: formatRs(parseFloat(s.stock_value) || 0),
        monthRevenue: formatRs(revenue),
        monthExpenditure: formatRs(expenditure),
        netProfit: revenue - expenditure,
      });
    } catch (error) {
      console.error('Error fetching chemical stats:', error);
      setChemError(true);
      setChemData({ products: '-', stockValue: '-', monthRevenue: '-', monthExpenditure: '-', netProfit: null });
    }
  };

  const fetchDairyStats = async () => {
  setDairyError(false);
  setDairyData({ animals: '...', todayProduction: '...', monthRevenue: '...', monthExpenditure: '...', netProfit: null });
  try {
    const response = await api.get('/api/dairy/summary');
    const s = response.data.data;

    setDairyData({
      animals: s.animals,
      todayProduction: `${parseFloat(s.today_production).toFixed(1)} L`,
      monthRevenue: formatRs(parseFloat(s.month_revenue)),
      monthExpenditure: formatRs(parseFloat(s.month_expenditure)),
      netProfit: parseFloat(s.net_profit),
    });
  } catch (error) {
    console.error('Error fetching dairy stats:', error);
    setDairyError(true);
    setDairyData({ animals: '-', todayProduction: '-', monthRevenue: '-', monthExpenditure: '-', netProfit: null });
  }
};

  const otherBusinesses = [
    { id: 'agriculture', name: 'Agriculture', icon: Sprout, color: '#27ae60', gradient: 'linear-gradient(135deg, #27ae60 0%, #229954 100%)', description: 'Crop cultivation & farm operations', stats: { acres: 200, crops: 8, revenue: 'Rs 5.18M' } },
    
  ];

  const chemNetProfitColor = chemData.netProfit === null ? '#95a5a6' : chemData.netProfit >= 0 ? '#27ae60' : '#e74c3c';

  const tapeNetProfitColor = tapeData.netProfit === null ? '#95a5a6' : tapeData.netProfit >= 0 ? '#27ae60' : '#e74c3c';

  const netProfitColor = dairyData.netProfit === null ? '#95a5a6' : dairyData.netProfit >= 0 ? '#27ae60' : '#e74c3c';

  const adminCardStyle = {
    background: 'white',
    borderRadius: '20px',
    cursor: 'pointer',
    transition: 'all 0.4s',
    boxShadow: '0 4px 15px rgba(0,0,0,0.08)',
    overflow: 'hidden',
    maxWidth: '500px',
  };

  return (
    <div style={{ minHeight: '100vh', background: '#f5f7fa', fontFamily: "'Inter', sans-serif" }}>
      <div style={{ background: 'linear-gradient(135deg, #1a4d2e 0%, #2d5016 100%)', color: 'white', padding: '24px 40px', boxShadow: '0 4px 20px rgba(0,0,0,0.1)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div>
          <h1 style={{ fontSize: '28px', margin: '0 0 4px 0', fontWeight: '700', fontFamily: "'Georgia', serif" }}>Business Control Center</h1>
          <p style={{ margin: 0, opacity: 0.9, fontSize: '14px' }}>Welcome back, {user?.name || 'Admin'}</p>
        </div>
        <div style={{ display: 'flex', gap: '12px' }}>
          <button onClick={() => navigate('/my-profile')} style={{ padding: '12px 24px', background: 'rgba(255,255,255,0.2)', border: '1px solid rgba(255,255,255,0.3)', borderRadius: '10px', color: 'white', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '8px', fontSize: '14px', fontWeight: '600' }} onMouseEnter={(e) => e.currentTarget.style.background = 'rgba(255,255,255,0.3)'} onMouseLeave={(e) => e.currentTarget.style.background = 'rgba(255,255,255,0.2)'}>👤 My Profile</button>
          <button onClick={logout} style={{ padding: '12px 24px', background: 'rgba(255,255,255,0.2)', border: '1px solid rgba(255,255,255,0.3)', borderRadius: '10px', color: 'white', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '8px', fontSize: '14px', fontWeight: '600' }} onMouseEnter={(e) => e.currentTarget.style.background = 'rgba(255,255,255,0.3)'} onMouseLeave={(e) => e.currentTarget.style.background = 'rgba(255,255,255,0.2)'}><LogOut size={18} /> Logout</button>
        </div>
      </div>

      <div style={{ padding: '40px' }}>
        <div style={{ marginBottom: '32px' }}>
          <h2 style={{ fontSize: '24px', color: '#2c3e50', marginBottom: '8px', fontWeight: '700', fontFamily: "'Georgia', serif" }}>Your Business Portfolio</h2>
          <p style={{ color: '#7f8c8d', fontSize: '15px' }}>Select a business module to view detailed analytics and management tools</p>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '24px', marginBottom: '40px' }}>

          {/* DAIRY FARM CARD */}
          <div onClick={() => navigate('/dairy')} style={{ background: 'white', borderRadius: '20px', cursor: 'pointer', transition: 'all 0.4s', boxShadow: '0 4px 15px rgba(0,0,0,0.08)', overflow: 'hidden', animation: 'fadeInUp 0.6s ease-out 0s backwards' }} onMouseEnter={(e) => { e.currentTarget.style.transform = 'translateY(-8px)'; e.currentTarget.style.boxShadow = '0 12px 30px rgba(0,0,0,0.15)'; }} onMouseLeave={(e) => { e.currentTarget.style.transform = 'translateY(0)'; e.currentTarget.style.boxShadow = '0 4px 15px rgba(0,0,0,0.08)'; }}>
            <div style={{ background: 'linear-gradient(135deg, #4a90e2 0%, #357abd 100%)', padding: '28px 24px', position: 'relative', overflow: 'hidden' }}>
              <div style={{ position: 'absolute', top: '-20px', right: '-20px', width: '120px', height: '120px', background: 'rgba(255,255,255,0.1)', borderRadius: '50%' }} />
              <div style={{ display: 'flex', alignItems: 'center', gap: '16px', position: 'relative' }}>
                <div style={{ width: '56px', height: '56px', background: 'rgba(255,255,255,0.25)', borderRadius: '16px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}><Milk size={32} color="white" /></div>
                <div>
                  <h3 style={{ fontSize: '22px', color: 'white', margin: '0 0 4px 0', fontWeight: '700', fontFamily: "'Georgia', serif" }}>Dairy Farm</h3>
                  <p style={{ color: 'rgba(255,255,255,0.9)', fontSize: '13px', margin: 0 }}>Milk production & livestock management</p>
                </div>
              </div>
            </div>
            <div style={{ padding: '20px 24px' }}>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px', marginBottom: '10px' }}>
                <div style={{ background: '#f8f9fa', borderRadius: '10px', padding: '12px', textAlign: 'center' }}>
                  <div style={{ fontSize: '22px', fontWeight: '700', color: '#2c3e50' }}>{dairyData.animals}</div>
                  <div style={{ fontSize: '11px', color: '#95a5a6', textTransform: 'uppercase' }}>Animals</div>
                </div>
                <div style={{ background: '#e8f5e9', borderRadius: '10px', padding: '12px', textAlign: 'center' }}>
                  <div style={{ fontSize: '20px', fontWeight: '700', color: '#1b5e20' }}>{dairyData.todayProduction}</div>
                  <div style={{ fontSize: '11px', color: '#95a5a6', textTransform: 'uppercase' }}>Today's Milk</div>
                </div>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px', marginBottom: '10px' }}>
                <div style={{ background: '#e3f2fd', borderRadius: '10px', padding: '12px', textAlign: 'center' }}>
                  <div style={{ fontSize: '18px', fontWeight: '700', color: '#1565c0' }}>{dairyData.monthRevenue}</div>
                  <div style={{ fontSize: '11px', color: '#95a5a6', textTransform: 'uppercase' }}>Revenue</div>
                </div>
                <div style={{ background: '#fff3e0', borderRadius: '10px', padding: '12px', textAlign: 'center' }}>
                  <div style={{ fontSize: '18px', fontWeight: '700', color: '#e65100' }}>{dairyData.monthExpenditure}</div>
                  <div style={{ fontSize: '11px', color: '#95a5a6', textTransform: 'uppercase' }}>Expenditure</div>
                </div>
              </div>
              <div style={{ background: dairyData.netProfit === null ? '#f8f9fa' : dairyData.netProfit >= 0 ? '#e8f5e9' : '#ffebee', border: `2px solid ${dairyData.netProfit === null ? '#e0e0e0' : dairyData.netProfit >= 0 ? '#a5d6a7' : '#ef9a9a'}`, borderRadius: '10px', padding: '12px', textAlign: 'center', marginBottom: '16px' }}>
                <div style={{ fontSize: '20px', fontWeight: '700', color: netProfitColor }}>
                  {dairyData.netProfit === null ? '...' : dairyData.netProfit >= 0 ? `+${formatRs(dairyData.netProfit)}` : `-${formatRs(Math.abs(dairyData.netProfit))}`}
                </div>
                <div style={{ fontSize: '11px', color: '#95a5a6', textTransform: 'uppercase' }}>Net Profit (This Month)</div>
              </div>
              {dairyError && (
                <div style={{ textAlign: 'center', marginBottom: '10px' }}>
                  <button onClick={(e) => { e.stopPropagation(); fetchDairyStats(); }} style={{ padding: '6px 16px', background: '#e74c3c', color: 'white', border: 'none', borderRadius: '8px', fontSize: '13px', cursor: 'pointer', marginBottom: '8px' }}>
                    🔄 Retry
                  </button>
                </div>
              )}
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px', color: '#4a90e2', fontWeight: '600', fontSize: '14px' }}>View Dashboard <ArrowRight size={16} /></div>
            </div>
          </div>

          {/* CHEMICALS CARD (live data) */}
          <div onClick={() => navigate('/chemicals')} style={{ background: 'white', borderRadius: '20px', cursor: 'pointer', transition: 'all 0.4s', boxShadow: '0 4px 15px rgba(0,0,0,0.08)', overflow: 'hidden', animation: 'fadeInUp 0.6s ease-out 0.1s backwards' }} onMouseEnter={(e) => { e.currentTarget.style.transform = 'translateY(-8px)'; e.currentTarget.style.boxShadow = '0 12px 30px rgba(0,0,0,0.15)'; }} onMouseLeave={(e) => { e.currentTarget.style.transform = 'translateY(0)'; e.currentTarget.style.boxShadow = '0 4px 15px rgba(0,0,0,0.08)'; }}>
            <div style={{ background: 'linear-gradient(135deg, #e74c3c 0%, #c0392b 100%)', padding: '28px 24px', position: 'relative', overflow: 'hidden' }}>
              <div style={{ position: 'absolute', top: '-20px', right: '-20px', width: '120px', height: '120px', background: 'rgba(255,255,255,0.1)', borderRadius: '50%' }} />
              <div style={{ display: 'flex', alignItems: 'center', gap: '16px', position: 'relative' }}>
                <div style={{ width: '56px', height: '56px', background: 'rgba(255,255,255,0.25)', borderRadius: '16px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}><Flame size={32} color="white" /></div>
                <div>
                  <h3 style={{ fontSize: '22px', color: 'white', margin: '0 0 4px 0', fontWeight: '700', fontFamily: "'Georgia', serif" }}>Chemicals</h3>
                  <p style={{ color: 'rgba(255,255,255,0.9)', fontSize: '13px', margin: 0 }}>Chemical manufacturing & distribution</p>
                </div>
              </div>
            </div>
            <div style={{ padding: '20px 24px' }}>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px', marginBottom: '10px' }}>
                <div style={{ background: '#f8f9fa', borderRadius: '10px', padding: '12px', textAlign: 'center' }}>
                  <div style={{ fontSize: '22px', fontWeight: '700', color: '#2c3e50' }}>{chemData.products}</div>
                  <div style={{ fontSize: '11px', color: '#95a5a6', textTransform: 'uppercase' }}>Products</div>
                </div>
                <div style={{ background: '#fdecea', borderRadius: '10px', padding: '12px', textAlign: 'center' }}>
                  <div style={{ fontSize: '18px', fontWeight: '700', color: '#c0392b' }}>{chemData.stockValue}</div>
                  <div style={{ fontSize: '11px', color: '#95a5a6', textTransform: 'uppercase' }}>Stock Value</div>
                </div>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px', marginBottom: '10px' }}>
                <div style={{ background: '#e3f2fd', borderRadius: '10px', padding: '12px', textAlign: 'center' }}>
                  <div style={{ fontSize: '18px', fontWeight: '700', color: '#1565c0' }}>{chemData.monthRevenue}</div>
                  <div style={{ fontSize: '11px', color: '#95a5a6', textTransform: 'uppercase' }}>Revenue</div>
                </div>
                <div style={{ background: '#fff3e0', borderRadius: '10px', padding: '12px', textAlign: 'center' }}>
                  <div style={{ fontSize: '18px', fontWeight: '700', color: '#e65100' }}>{chemData.monthExpenditure}</div>
                  <div style={{ fontSize: '11px', color: '#95a5a6', textTransform: 'uppercase' }}>Expenditure</div>
                </div>
              </div>
              <div style={{ background: chemData.netProfit === null ? '#f8f9fa' : chemData.netProfit >= 0 ? '#e8f5e9' : '#ffebee', border: `2px solid ${chemData.netProfit === null ? '#e0e0e0' : chemData.netProfit >= 0 ? '#a5d6a7' : '#ef9a9a'}`, borderRadius: '10px', padding: '12px', textAlign: 'center', marginBottom: '16px' }}>
                <div style={{ fontSize: '20px', fontWeight: '700', color: chemNetProfitColor }}>
                  {chemData.netProfit === null ? '...' : chemData.netProfit >= 0 ? `+${formatRs(chemData.netProfit)}` : `-${formatRs(Math.abs(chemData.netProfit))}`}
                </div>
                <div style={{ fontSize: '11px', color: '#95a5a6', textTransform: 'uppercase' }}>Net Profit (This Month)</div>
              </div>
              {chemError && (
                <div style={{ textAlign: 'center', marginBottom: '10px' }}>
                  <button onClick={(e) => { e.stopPropagation(); fetchChemStats(); }} style={{ padding: '6px 16px', background: '#e74c3c', color: 'white', border: 'none', borderRadius: '8px', fontSize: '13px', cursor: 'pointer', marginBottom: '8px' }}>
                    🔄 Retry
                  </button>
                </div>
              )}
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px', color: '#e74c3c', fontWeight: '600', fontSize: '14px' }}>View Dashboard <ArrowRight size={16} /></div>
            </div>
          </div>

          {/* ADHESIVE TAPES CARD (live data) */}
          <div onClick={() => navigate('/adhesive')} style={{ background: 'white', borderRadius: '20px', cursor: 'pointer', transition: 'all 0.4s', boxShadow: '0 4px 15px rgba(0,0,0,0.08)', overflow: 'hidden', animation: 'fadeInUp 0.6s ease-out 0.2s backwards' }} onMouseEnter={(e) => { e.currentTarget.style.transform = 'translateY(-8px)'; e.currentTarget.style.boxShadow = '0 12px 30px rgba(0,0,0,0.15)'; }} onMouseLeave={(e) => { e.currentTarget.style.transform = 'translateY(0)'; e.currentTarget.style.boxShadow = '0 4px 15px rgba(0,0,0,0.08)'; }}>
            <div style={{ background: 'linear-gradient(135deg, #f39c12 0%, #e67e22 100%)', padding: '28px 24px', position: 'relative', overflow: 'hidden' }}>
              <div style={{ position: 'absolute', top: '-20px', right: '-20px', width: '120px', height: '120px', background: 'rgba(255,255,255,0.1)', borderRadius: '50%' }} />
              <div style={{ display: 'flex', alignItems: 'center', gap: '16px', position: 'relative' }}>
                <div style={{ width: '56px', height: '56px', background: 'rgba(255,255,255,0.25)', borderRadius: '16px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}><Package size={32} color="white" /></div>
                <div>
                  <h3 style={{ fontSize: '22px', color: 'white', margin: '0 0 4px 0', fontWeight: '700', fontFamily: "'Georgia', serif" }}>Adhesive Tapes</h3>
                  <p style={{ color: 'rgba(255,255,255,0.9)', fontSize: '13px', margin: 0 }}>Industrial tape manufacturing</p>
                </div>
              </div>
            </div>
            <div style={{ padding: '20px 24px' }}>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px', marginBottom: '10px' }}>
                <div style={{ background: '#f8f9fa', borderRadius: '10px', padding: '12px', textAlign: 'center' }}>
                  <div style={{ fontSize: '22px', fontWeight: '700', color: '#2c3e50' }}>{tapeData.products}</div>
                  <div style={{ fontSize: '11px', color: '#95a5a6', textTransform: 'uppercase' }}>Pieces in stock</div>
                </div>
                <div style={{ background: '#fef5e7', borderRadius: '10px', padding: '12px', textAlign: 'center' }}>
                  <div style={{ fontSize: '18px', fontWeight: '700', color: '#b9770e' }}>{tapeData.stockValue}</div>
                  <div style={{ fontSize: '11px', color: '#95a5a6', textTransform: 'uppercase' }}>Stock Value</div>
                </div>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px', marginBottom: '10px' }}>
                <div style={{ background: '#e3f2fd', borderRadius: '10px', padding: '12px', textAlign: 'center' }}>
                  <div style={{ fontSize: '18px', fontWeight: '700', color: '#1565c0' }}>{tapeData.monthRevenue}</div>
                  <div style={{ fontSize: '11px', color: '#95a5a6', textTransform: 'uppercase' }}>Revenue</div>
                </div>
                <div style={{ background: '#fff3e0', borderRadius: '10px', padding: '12px', textAlign: 'center' }}>
                  <div style={{ fontSize: '18px', fontWeight: '700', color: '#e65100' }}>{tapeData.monthExpenditure}</div>
                  <div style={{ fontSize: '11px', color: '#95a5a6', textTransform: 'uppercase' }}>Expenditure</div>
                </div>
              </div>
              <div style={{ background: tapeData.netProfit === null ? '#f8f9fa' : tapeData.netProfit >= 0 ? '#e8f5e9' : '#ffebee', border: `2px solid ${tapeData.netProfit === null ? '#e0e0e0' : tapeData.netProfit >= 0 ? '#a5d6a7' : '#ef9a9a'}`, borderRadius: '10px', padding: '12px', textAlign: 'center', marginBottom: '16px' }}>
                <div style={{ fontSize: '20px', fontWeight: '700', color: tapeNetProfitColor }}>
                  {tapeData.netProfit === null ? '...' : tapeData.netProfit >= 0 ? `+${formatRs(tapeData.netProfit)}` : `-${formatRs(Math.abs(tapeData.netProfit))}`}
                </div>
                <div style={{ fontSize: '11px', color: '#95a5a6', textTransform: 'uppercase' }}>Net Profit (This Month)</div>
              </div>
              {tapeError && (
                <div style={{ textAlign: 'center', marginBottom: '10px' }}>
                  <button onClick={(e) => { e.stopPropagation(); fetchTapeStats(); }} style={{ padding: '6px 16px', background: '#e74c3c', color: 'white', border: 'none', borderRadius: '8px', fontSize: '13px', cursor: 'pointer', marginBottom: '8px' }}>
                    🔄 Retry
                  </button>
                </div>
              )}
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px', color: '#f39c12', fontWeight: '600', fontSize: '14px' }}>View Dashboard <ArrowRight size={16} /></div>
            </div>
          </div>

          {/* OTHER BUSINESS CARDS */}
          {otherBusinesses.map((business, index) => {
            const IconComponent = business.icon;
            return (
              <div key={business.id} onClick={() => navigate(`/${business.id}`)} style={{ background: 'white', borderRadius: '20px', cursor: 'pointer', transition: 'all 0.4s', boxShadow: '0 4px 15px rgba(0,0,0,0.08)', overflow: 'hidden', animation: `fadeInUp 0.6s ease-out ${(index + 1) * 0.1}s backwards` }} onMouseEnter={(e) => { e.currentTarget.style.transform = 'translateY(-8px)'; e.currentTarget.style.boxShadow = '0 12px 30px rgba(0,0,0,0.15)'; }} onMouseLeave={(e) => { e.currentTarget.style.transform = 'translateY(0)'; e.currentTarget.style.boxShadow = '0 4px 15px rgba(0,0,0,0.08)'; }}>
                <div style={{ background: business.gradient, padding: '28px 24px', position: 'relative', overflow: 'hidden' }}>
                  <div style={{ position: 'absolute', top: '-20px', right: '-20px', width: '120px', height: '120px', background: 'rgba(255,255,255,0.1)', borderRadius: '50%' }} />
                  <div style={{ display: 'flex', alignItems: 'center', gap: '16px', position: 'relative' }}>
                    <div style={{ width: '56px', height: '56px', background: 'rgba(255,255,255,0.25)', borderRadius: '16px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}><IconComponent size={32} color="white" /></div>
                    <div>
                      <h3 style={{ fontSize: '22px', color: 'white', margin: '0 0 4px 0', fontWeight: '700', fontFamily: "'Georgia', serif" }}>{business.name}</h3>
                      <p style={{ color: 'rgba(255,255,255,0.9)', fontSize: '13px', margin: 0 }}>{business.description}</p>
                    </div>
                  </div>
                </div>
                <div style={{ padding: '24px' }}>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '16px', marginBottom: '20px' }}>
                    {Object.entries(business.stats).map(([key, value]) => (
                      <div key={key} style={{ textAlign: 'center' }}>
                        <div style={{ fontSize: '16px', fontWeight: '700', color: '#2c3e50', marginBottom: '4px' }}>{value}</div>
                        <div style={{ fontSize: '11px', color: '#95a5a6', textTransform: 'uppercase', letterSpacing: '0.5px' }}>{key}</div>
                      </div>
                    ))}
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px', color: business.color, fontWeight: '600', fontSize: '14px' }}>View Dashboard <ArrowRight size={16} /></div>
                </div>
              </div>
            );
          })}
        </div>

        {/* Admin-only cards */}
        {user?.role === 'businessman' && (
          <div style={{ display: 'flex', gap: '24px', flexWrap: 'wrap' }}>

            {/* HR Management Card */}
            <div
              onClick={() => navigate('/hr')}
              style={adminCardStyle}
              onMouseEnter={(e) => { e.currentTarget.style.transform = 'translateY(-8px)'; e.currentTarget.style.boxShadow = '0 12px 30px rgba(0,0,0,0.15)'; }}
              onMouseLeave={(e) => { e.currentTarget.style.transform = 'translateY(0)'; e.currentTarget.style.boxShadow = '0 4px 15px rgba(0,0,0,0.08)'; }}
            >
              <div style={{ background: 'linear-gradient(135deg, #11998e 0%, #38ef7d 100%)', padding: '28px 24px', position: 'relative', overflow: 'hidden' }}>
                <div style={{ position: 'absolute', top: '-20px', right: '-20px', width: '120px', height: '120px', background: 'rgba(255,255,255,0.1)', borderRadius: '50%' }} />
                <div style={{ display: 'flex', alignItems: 'center', gap: '16px', position: 'relative' }}>
                  <div style={{ width: '56px', height: '56px', background: 'rgba(255,255,255,0.25)', borderRadius: '16px', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '32px' }}>👷</div>
                  <div>
                    <h3 style={{ fontSize: '22px', color: 'white', margin: '0 0 4px 0', fontWeight: '700', fontFamily: "'Georgia', serif" }}>HR Management</h3>
                    <p style={{ color: 'rgba(255,255,255,0.9)', fontSize: '13px', margin: 0 }}>Employees, Salaries & Attendance</p>
                  </div>
                </div>
              </div>
              <div style={{ padding: '24px' }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px', color: '#11998e', fontWeight: '600', fontSize: '14px' }}>
                  Manage HR <ArrowRight size={16} />
                </div>
              </div>
            </div>

            {/* Users Management Card */}
            <div
              onClick={() => navigate('/users')}
              style={adminCardStyle}
              onMouseEnter={(e) => { e.currentTarget.style.transform = 'translateY(-8px)'; e.currentTarget.style.boxShadow = '0 12px 30px rgba(0,0,0,0.15)'; }}
              onMouseLeave={(e) => { e.currentTarget.style.transform = 'translateY(0)'; e.currentTarget.style.boxShadow = '0 4px 15px rgba(0,0,0,0.08)'; }}
            >
              <div style={{ background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', padding: '28px 24px', position: 'relative', overflow: 'hidden' }}>
                <div style={{ position: 'absolute', top: '-20px', right: '-20px', width: '120px', height: '120px', background: 'rgba(255,255,255,0.1)', borderRadius: '50%' }} />
                <div style={{ display: 'flex', alignItems: 'center', gap: '16px', position: 'relative' }}>
                  <div style={{ width: '56px', height: '56px', background: 'rgba(255,255,255,0.25)', borderRadius: '16px', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '32px' }}>👥</div>
                  <div>
                    <h3 style={{ fontSize: '22px', color: 'white', margin: '0 0 4px 0', fontWeight: '700', fontFamily: "'Georgia', serif" }}>Users Management</h3>
                    <p style={{ color: 'rgba(255,255,255,0.9)', fontSize: '13px', margin: 0 }}>Manage system users and permissions</p>
                  </div>
                </div>
              </div>
              <div style={{ padding: '24px' }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px', color: '#667eea', fontWeight: '600', fontSize: '14px' }}>
                  Manage Users <ArrowRight size={16} />
                </div>
              </div>
            </div>

          </div>
        )}
      </div>

      <style>{`
        @keyframes fadeInUp {
          from { opacity: 0; transform: translateY(30px); }
          to { opacity: 1; transform: translateY(0); }
        }
      `}</style>
    </div>
  );
};

export default MainDashboard;